import React, { useState, useEffect, useRef } from 'react';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { ThemeProvider } from 'styled-components';
import {
  LoggerProvider,
  lightTheme,
} from 'amazon-chime-sdk-component-library-react';
import { ConsoleLogger, LogLevel } from 'amazon-chime-sdk-js';
import 'src/latest/src/core/i18';
import { routes } from '../../latest/src/routes';
import ModalContainer from '../../components/base/ModalContainer/ModalContainer';
import AppLoadingContainer from '../AppLoading/AppLoadingContainer';
import Offline from '../../components/base/Offline/Offline';
import './app.scss';
import FlashNotification from '../../latest/src/components/FlashNotification/FlashNotification';
import { clearModalContent } from '../../redux/modules/modalEvent';
import { UserServiceSection } from '../../latest/src/constants/AppServerConfig';
import { useIdleTimer } from 'react-idle-timer';
import { checkAuthorization } from '../../latest/src/utils/Authentications';
import { GetUserLogOut } from 'src/api/authentications';
import { fetchUserInformationByIdV1 } from '../../api/user';
import { ErrorBoundary } from 'react-error-boundary';
import LoggingErrorScreen from 'src/latest/src/features/logging-error/LoggingErrorScreen';
import '../../styles/styles.scss';
import {
  setFlashNotification,
  removeFlashNotification,
} from '../../redux/modules/flashNotification';
import _ from 'lodash';
import { LoadingIndicator } from 'src/latest/src/components/LoadingIndicator';
import withClearCache from '../../ClearCache';
import {
  isNoHeaderRoute,
  isNonAuthRoute,
  isShouldLoggoutAndRedirectRoute,
} from 'src/latest/src/constants/AppRoutesExpressions';
import {
  setAppConfig,
  toggleAppConfigLoader,
} from 'src/redux/actions/configurationActions';
import { generateAppConfig } from 'src/latest/src/core';
import { getAppConfig } from 'src/redux/services/commonServices';
import {
  AppConstant,
  Header,
  IDLE_PROMPT_BEFORE_IDLE_MS,
  IDLE_TIMEOUT_MS,
} from 'src/latest/src';
import TeletherapySocket from 'src/components/modules/TeletherapySocket/TeletherapySocket';
import UserServiceSocket from 'src/components/modules/UserServiceSocket/UserServiceSocket';
import { Logger } from 'src/latest/src/services'; // Add correct Logger import
import { analyticsService } from 'src/latest/src/services/AnalyticsService';

import '@react-pdf-viewer/core/lib/styles/index.css';
import '@react-pdf-viewer/default-layout/lib/styles/index.css';
import InactivityWarningMessage from 'src/latest/src/components/InactivityWarningMessage/InactivityWarningMessage';
import TermsAcceptanceModal from 'src/latest/src/features/terms-acceptance/TermsAcceptanceModal';

const ClearCacheComponent = withClearCache(MainApp);
const loading = () => <div />;

const consoleLogger = new ConsoleLogger('Chime Logs', LogLevel.OFF);

function MainApp() {
  const dispatch = useDispatch();
  const location = useLocation(); // Uncommented useLocation hook
  const isOnline = true;
  const isHeaderVisible = useSelector(
    state => state?.questionnaireData?.currentConfig?.header.visible,
  );
  const navigate = useNavigate();
  const popupLoaderStatus = useSelector(
    state => state?.configuration?.showPopupLoader,
  );
  const tenantBackgroundColor = useSelector(
    state => state?.configuration?.tenantBackgroundColor,
  );
  const { isAuthed, loggedInUser } = useSelector(
    ({ authentication }) => authentication,
  );
  const [appConfigLoaded, setAppConfigLoaded] = useState(false);
  const { title, modalContent, onToggle, ...modalEvent } = useSelector(
    ({ modalEvent }) => modalEvent,
  );
  const [profileInfo, setProfileInfo] = useState(loggedInUser);
  const profileType = window.location.pathname.includes('client')
    ? 'client'
    : 'therapist';
  const profileId = localStorage.getItem('profileId');
  // Tracks the id of the currently-shown inactivity warning flash
  // notification, so it can be dismissed the moment activity resumes.
  const inactivityWarningIdRef = useRef(null);
  const [shouldShowHeader, setShouldShowHeader] = useState(true);
  const [enableSocket, setEnableSocket] = useState(false);
  const [isMetaAdsInitialized, setIsMetaAdsInitialized] = useState(false);
  const [isTikTokPixelInitialized, setIsTikTokPixelInitialized] =
    useState(false);
  const [isSnapChatInitialized, setIsSnapChatInitialized] = useState(false);

  // Scroll to top if path changes and handle Meta Ads tracking
  useEffect(() => {
    document.body.scrollTop = 0; // enforcing the page to always scroll to top when a new page open

    // check if snap chat pixel is initialized
    if (
      isSnapChatInitialized &&
      location.pathname.includes('/verify-onboarding')
    ) {
      try {
        // Track ViewContent specifically for onboarding verification
        window.snaptr('track', 'PAGE_VIEW');
        window.snaptr('track', 'SIGN_UP', {});
      } catch (error) {
        Logger.error('Error tracking SnapChat Pixel events:', error);
      }
    }

    // check if tik tok pixel is initialized
    if (
      isTikTokPixelInitialized &&
      location.pathname.includes('/verify-onboarding')
    ) {
      try {
        // Track ViewContent specifically for onboarding verification
        window.ttq.track('ViewContent', {
          content_name: 'Onboarding Verification',
          content_category: 'Onboarding',
          content_path: location.pathname,
        });

        window.ttq.page();
      } catch (error) {
        Logger.error('Error tracking TikTok Pixel events:', error);
      }
    }

    // Google Tag manager custom event added

    if (location.pathname.includes('/verify-onboarding')) {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'verify_onboarding_pageview',
      });
    }

    // Only attempt to track if Meta Ads is initialized
    if (
      isMetaAdsInitialized &&
      location.pathname.includes('/verify-onboarding')
    ) {
      try {
        // Track ViewContent specifically for onboarding verification
        if (location.pathname.includes('/verify-onboarding')) {
          Logger.log('Tracking ViewContent for onboarding verification');
          window.fbq('track', 'ViewContent', {
            content_name: 'Onboarding Verification',
            content_category: 'Onboarding',
            content_path: location.pathname,
          });
        }
      } catch (error) {
        Logger.error('Error tracking Meta Ads events:', error);
      }
    }

    // this is added to check if user is logged in and tries to access non-auth routes
    // then the app should not allow to access those routes.
    if (isAuthed && isNonAuthRoute(location.pathname)) {
      navigate('/');
    }
  }, [location.pathname, isMetaAdsInitialized]);

  useEffect(() => {
    const invalidOrigins = window.location.origin.includes('global');

    // Wait for the id, not just any identity: login dispatches the bare /token
    // response before the profile, and the sockets send the id once, on mount.
    if (loggedInUser?.id && !invalidOrigins) {
      setEnableSocket(true);
    } else {
      setEnableSocket(false);
    }
  }, [window.location.pathname, loggedInUser]);

  // Getting the APP Config
  const fetchAppConfig = async () => {
    try {
      dispatch(toggleAppConfigLoader(true));
      const appConfig = await getAppConfig();
      const clientAppConfig = generateAppConfig(appConfig?.config_values);

      sessionStorage.setItem('TENANT_ID', clientAppConfig.tenantId);
      analyticsService.initializeGoogleTagManager(clientAppConfig);
      const metaAdsInitialized = await analyticsService.initializeMetaAdsScript(
        clientAppConfig,
      );
      const tiktokPixelInitialized =
        await analyticsService.initializeTikTokPixel(clientAppConfig);
      const snapChatInitialized = await analyticsService.initializeSnapPixel(
        clientAppConfig,
      );

      if (snapChatInitialized === true) {
        setIsSnapChatInitialized(true);
      }

      if (metaAdsInitialized === true) {
        setIsMetaAdsInitialized(true);
      }

      if (tiktokPixelInitialized === true) {
        setIsTikTokPixelInitialized(true);
      }

      dispatch(setAppConfig(clientAppConfig));
    } catch (error) {
      Logger.error('Failed to load app config', error);
    } finally {
      initApp();
      dispatch(toggleAppConfigLoader(false));
      setAppConfigLoaded(true);
    }
  };

  useEffect(() => {
    window?.addEventListener('scroll', handleScroll, true);

    fetchAppConfig();

    return () => {
      window?.removeEventListener('scroll', handleScroll, true);
    };
  }, []);

  useEffect(() => {
    setShouldShowHeader(!isNoHeaderRoute(location.pathname));
  }, [location]);

  const initApp = () => {
    handleRedirections();

    if (!profileInfo || _.isEmpty(profileInfo)) {
      onGetProfileInfo(profileType, profileId);
    }
  };

  const handleRedirections = () => {
    if (isAuthed) {
      if (isShouldLoggoutAndRedirectRoute(window.location.pathname)) {
        handleLogout('SESSION_EXPIRED', false, true);
      }
    }
  };

  const handleCloseModal = () => {
    dispatch(clearModalContent());
    onToggle && onToggle();
  };

  const handleLogout = async (
    type,
    showMessage?: boolean,
    disableRedirect?: boolean,
  ) => {
    await GetUserLogOut(loggedInUser.id, type, showMessage);

    if (!disableRedirect) {
      nextPath('/login');
    }
  };

  const nextPath = (path: string) => {
    return navigate(path);
  };

  const handleIdlePrompt = () => {
    const isTeletherapyRoute =
      window.location.pathname.includes('/teletherapy');

    // Mirrors the teletherapy exemption in handleIdle below — an active
    // video session should never surface the "you'll be logged out" warning.
    if (isTeletherapyRoute) {
      idleTimer.reset();

      return;
    }

    const action = setFlashNotification({
      message: (
        <InactivityWarningMessage
          initialSeconds={IDLE_PROMPT_BEFORE_IDLE_MS / 1000}
        />
      ),
      duration: IDLE_PROMPT_BEFORE_IDLE_MS,
      dedupeKey: 'inactivity_logout_warning',
    });

    inactivityWarningIdRef.current = action?.payload?.id || null;
    dispatch(action);
  };

  // Fires on every tracked activity event, even while the warning is up.
  // If the user is genuinely active during the prompt window, cancel the
  // pending logout and dismiss the warning — otherwise moving the mouse
  // during the warning would do nothing, which is confusing.
  const handleIdleAction = () => {
    if (idleTimer.isPrompted()) {
      idleTimer.activate();

      if (inactivityWarningIdRef.current) {
        dispatch(removeFlashNotification(inactivityWarningIdRef.current));
        inactivityWarningIdRef.current = null;
      }
    }
  };

  const handleIdle = async () => {
    const isTeletherapyRoute =
      window.location.pathname.includes('/teletherapy');

    // If we're on any teletherapy sub-route, keep the timer alive
    if (isTeletherapyRoute) {
      idleTimer.reset();

      return;
    }

    dispatch(clearModalContent());
    await handleLogout('SESSION_EXPIRED');
  };

  const handleScroll = () => {
    const collapseHeader = document.querySelector('button.navbar-toggler');
    const collapseMenu = document.getElementById('responsive-navbar-nav');

    if (collapseHeader && collapseMenu) {
      collapseHeader.classList.add('collapsed');
      collapseMenu.classList.remove('show');
    }
  };

  //    useEffect(() => {
  //        const unlisten = navigate(() => {
  //            // Check for a new service worker
  //            checkServiceWorkerUpdates();
  //        });

  //        return () => {
  //            unlisten(); // Clean up the listener when the component unmounts
  //        };
  //    }, [navigate]);

  const onGetProfileInfo = async (type, id) => {
    try {
      if (isAuthed && id && loggedInUser?.user_role) {
        const resp = await fetchUserInformationByIdV1(
          id,
          UserServiceSection.profile_display,
          'medium'
        );

        setProfileInfo(resp);
      }
    } catch (error) {
      dispatch(
        setFlashNotification({
          message:
            error?.response?.data?.message ||
            'Something went wrong please try again!',
          isError: true,
          dispatchClass: 'notif-settings',
        }),
      );
    }
  };

  // Called directly here (not from a nested component) so the idle timer's
  // internal state survives MainApp re-renders. A previous version wrapped
  // this in a locally-defined <IdleTimerComponent/>, which got a new
  // function identity — and was fully unmounted/remounted, losing all
  // internal state — on every unrelated MainApp re-render.
  // NOTE: useIdleTimer's `ref` option only wires up when going through the
  // library's own withIdleTimer/class-component paths — calling the hook
  // directly (as we do here) never assigns to it. The hook's *return
  // value* is the actual API object; that's what handleIdlePrompt/
  // handleIdleAction/handleIdle above close over as `idleTimer`.
  const idleTimer = useIdleTimer({
    element: document,
    onPrompt: handleIdlePrompt,
    onIdle: handleIdle,
    onAction: handleIdleAction,
    debounce: 250,
    timeout: IDLE_TIMEOUT_MS,
    promptBeforeIdle: IDLE_PROMPT_BEFORE_IDLE_MS,
    disabled: !(isAuthed && checkAuthorization()),
  });

  return (
    <ErrorBoundary FallbackComponent={LoggingErrorScreen}>
      <LoadingIndicator loading={popupLoaderStatus || !appConfigLoaded} />
      {appConfigLoaded && (
        <AppLoadingContainer>
          {!isOnline && !window.location.pathname.includes('/teletherapy') && (
            <ModalContainer
              className="offline-modal text-center"
              isShow={true}
              size="lg"
            >
              <Offline variant="light" />
            </ModalContainer>
          )}
          <div
            style={{
              backgroundColor:
                tenantBackgroundColor || AppConstant.defaultAppBackgroundColor,
            }}
            className={`page-container ${
              window.location.pathname.includes('meeting-room')
                ? 'remove-top-margin'
                : ''
            }`}
          >
            {shouldShowHeader && isHeaderVisible && (
              <Header className="bg-white" onLogout={handleLogout} />
            )}

            <ThemeProvider theme={lightTheme}>
              <LoggerProvider logger={consoleLogger}>
                <React.Suspense fallback={loading()}>
                  <Routes>
                    {routes.map((route, i) => (
                      <Route
                        key={i} // Ensure unique keys
                        exact={route.exact}
                        path={route.path}
                        element={
                          <route.component loggedInUser={route.loggedInUser} />
                        }
                      />
                    ))}
                  </Routes>
                </React.Suspense>
              </LoggerProvider>
            </ThemeProvider>
          </div>
          {enableSocket && (
            <>
              {/* On every route, so a therapist hears a client arrive wherever they are. */}
              <TeletherapySocket
                key={loggedInUser.id}
                loggedInUser={loggedInUser}
              />
              <UserServiceSocket
                key={loggedInUser.id}
                loggedInUser={loggedInUser}
              />
            </>
          )}
          <FlashNotification />
          <ModalContainer
            {...modalEvent}
            header={title}
            isShow={!!modalContent}
            toggle={handleCloseModal}
          >
            {modalContent}
          </ModalContainer>
          <TermsAcceptanceModal onLogout={handleLogout} />
        </AppLoadingContainer>
      )}
    </ErrorBoundary>
  );
}

// A special wrapper for <Route> that knows how to
// handle "sub"-routes by passing them in a `routes`
// prop to the component it renders.
export function RouteWithSubRoutes(route) {
  return route.routes ? (
    <>
      {route.routes.map((nestedRoute, i) => (
        <Route
          key={i}
          path={nestedRoute.path}
          element={
            <nestedRoute.component
              routes={nestedRoute.routes}
              loggedInUser={nestedRoute.loggedInUser}
            />
          }
        />
      ))}
    </>
  ) : (
    <Route
      path={route.path}
      element={
        <route.component
          routes={route.routes}
          loggedInUser={route.loggedInUser}
        />
      }
    />
  );
}

function App() {
  return <ClearCacheComponent />;
}

export default App;
